import React, { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DataTable from "examples/Tables/DataTable";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Card, Chip, Box } from "@mui/material";
import { Image } from "@mui/icons-material";
import { getCategories } from "services/categoriesService";
import CategoryModal from "./Modals/categoryModal";
import SubCategoryModal from "./Modals/subCategoryModal";
import DeleteCategoryModal from "./Modals/deleteCategoryModal";
import CircularProgress from "@mui/material/CircularProgress";
import ViewSubCategoriesModal from "./Modals/viewSubCategoriesModal";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import "./Categories.css";
import SearchBar from "../Components/SearchBar";
function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    category: null,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openAddCategory, setOpenAddCategory] = useState(false);
  const [openAddSubCategory, setOpenAddSubCategory] = useState(false);
  const [openViewSubCategories, setOpenViewSubCategories] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);
  const handleMenuOpen = (event, category) => {
    event.preventDefault();
    const menuWidth = 160;
    setMenu({
      visible: true,
      x: event.clientX - menuWidth,
      y: event.clientY,
      category,
    });
  };
  const handleMenuClose = () => {
    setMenu({ ...menu, visible: false, category: null });
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setOpenAddCategory(true);
  };

  const handleAddSubCategory = () => {
    setSelectedCategory(null);
    setOpenAddSubCategory(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setOpenAddCategory(true);
    handleMenuClose();
  };

  const handleCloseModal = () => {
    setOpenAddCategory(false);
    setOpenAddSubCategory(false);
    setOpenViewSubCategories(false);
    setSelectedCategory(null);
    setOpenDeleteCategory(false);
  };

  const handleCategorySuccess = (updatedCategory) => {
    // Refresh the categories list
    fetchCategories();
  };

  const handleViewSubCategories = (category) => {
    setSelectedCategory(category);
    setOpenViewSubCategories(true);
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getCategories(debouncedSearchTerm || null);
      if (response.success && response.data) {
        setCategories(response.data);
      } else {
        console.error("Failed to fetch categories:", response.message);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row }) => (
        <ArgonBox display="flex" alignItems="center">
          <Box
            style={{
              width: 50,
              height: 50,
              marginRight: 10,
              borderRadius: 15,
              border: "1px solid #e0e0e0",
              backgroundColor: "#f2f2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {row.original.icon ? (
              <img
                src={row.original.icon}
                alt="icon"
                style={{
                  borderRadius: 5,
                  width: "60%",
                  height: "60%",
                }}
              />
            ) : (
              <Image fontSize="large" color="primary" />
            )}
          </Box>

          <ArgonTypography fontWeight="medium" variant="h5">
            {row.original.name}
          </ArgonTypography>
        </ArgonBox>
      ),
    },
    {
      Header: "Sub-Categories",
      accessor: "subcategories",
      Cell: ({ row }) => {
        const subCats = row.original.subcategories || [];
        const hasSubCategories = subCats.length > 0;

        return (
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            minWidth="600px"
          >
            <ArgonBox
              display="flex"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              flex={1}
            >
              {subCats.slice(0, 5).map((sub, idx) => (
                <Chip
                  key={sub._id}
                  label={sub.name}
                  size="medium"
                  sx={{
                    fontWeight: "bold",
                    background: "#f2f2f2",
                    color: "#2D3F50",
                  }}
                />
              ))}
              {subCats.length > 5 && (
                <ArgonTypography variant="body2" color="textSecondary">
                  +{subCats.length - 5} more
                </ArgonTypography>
              )}
            </ArgonBox>
            {hasSubCategories && (
              <ArgonTypography
                className="flagged-content-table-text view-details-text"
                fontWeight="medium"
                onClick={() => handleViewSubCategories(row.original)}
              >
                View Details
              </ArgonTypography>
            )}
          </ArgonBox>
        );
      },
    },
    {
      Header: "Action",
      accessor: "action",
      Cell: ({ row }) => (
        <IconButton onClick={(e) => handleMenuOpen(e, row.original)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    fetchCategories();
  }, [debouncedSearchTerm]);

  return (
    <>
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search (sub)categories"
      />
      <ArgonBox display="flex" justifyContent="flex-end" py={3} gap={2}>
        <ArgonButton
          variant="outlined"
          color="#ffffff"
          onClick={handleAddSubCategory}
        >
          Add Sub-Category
        </ArgonButton>
        <ArgonButton
          variant="outlined"
          color="#ffffff"
          onClick={handleAddCategory}
        >
          Add New Category
        </ArgonButton>
      </ArgonBox>
      <ArgonBox>
        <Card sx={{ overflow: "visible", marginBottom: 10, padding: 3 }}>
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            p={3}
          >
            <ArgonTypography variant="h5" fontWeight="medium">
              Categories
            </ArgonTypography>
          </ArgonBox>
          <ArgonBox>
            {loading ? (
              <ArgonBox display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </ArgonBox>
            ) : (
              <DataTable
                table={{ columns, rows: categories }}
                entriesPerPage={false}
                canSearch={false}
                showTotalEntries={false}
                pagination={false}
              />
            )}
          </ArgonBox>
          {menu.visible && (
            <div
              className="categories:context-menu"
              style={{ top: menu.y, left: menu.x, position: "fixed" }}
              onMouseLeave={handleMenuClose}
            >
              <div
                className="categories:menu-item"
                onClick={() => {
                  handleMenuClose();
                  setSelectedCategory({
                    name: menu.category.name,
                    value: menu.category._id,
                  });
                  setOpenAddSubCategory(true);
                }}
              >
                Create Sub Category
              </div>
              <div
                className="categories:menu-item"
                onClick={() => handleEditCategory(menu.category)}
              >
                Edit Category
              </div>
              <div
                className="categories:menu-item categories:delete"
                onClick={() => {
                  handleMenuClose();
                  setOpenDeleteCategory(true);
                  setSelectedCategory({
                    name: menu.category.name,
                    value: menu.category._id,
                  });
                }}
              >
                Delete Category
              </div>
            </div>
          )}

          {openAddCategory && (
            <CategoryModal
              open={openAddCategory}
              onClose={handleCloseModal}
              category={selectedCategory}
              onSuccess={handleCategorySuccess}
            />
          )}

          {openAddSubCategory && (
            <SubCategoryModal
              open={openAddSubCategory}
              onClose={handleCloseModal}
              category={selectedCategory}
              onSuccess={handleCategorySuccess}
            />
          )}

          {openDeleteCategory && (
            <DeleteCategoryModal
              open={openDeleteCategory}
              onClose={handleCloseModal}
              category={selectedCategory}
              onSuccess={handleCategorySuccess}
            />
          )}

          {openViewSubCategories && (
            <ViewSubCategoriesModal
              open={openViewSubCategories}
              onClose={handleCloseModal}
              category={selectedCategory}
              onSuccess={handleCategorySuccess}
            />
          )}
        </Card>
      </ArgonBox>
    </>
  );
}

export default Categories;
